// App.tsx -- Root component with HashRouter, auth gate, theme state.
// 5-surface team IA (Briefing/Map/People/Sources/Settings) + redirects; data-theme theming.

import { useState, useEffect, useRef, useMemo } from 'react';
import { HashRouter, Routes, Route, Navigate, useNavigate, useLocation, useParams, useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { supabase, isDevBypass, onAuthChange, setAccessToken } from './lib/auth';
import { isNotFound, isUnauthorized } from './lib/api';
import { resolveInitialTheme, applyTheme, type Theme } from './lib/theme';
import {
  readBriefingTokenFromUrl,
  stripBriefingTokenFromUrl,
  stashBriefingTokenForLogin,
  popStashedBriefingToken,
  verifyBriefingIntent,
  reconcileBriefingIntent,
} from './lib/briefing-intent';
import { pageview, identifyUser, setTeamGroup, resetUser, track } from './lib/analytics';
import { useSessionReplayScope } from './hooks/useSessionReplayScope';

// Shell
import { TopNav } from './components/shell/TopNav';
import { BottomTabBar } from './components/shell/BottomTabBar';
import { useIsMobile } from './hooks/useIsMobile';
import { InvitePage } from './components/InvitePage';
import { CmdK } from './components/shared/CmdK';
import { ErrorBoundary } from './components/shared/ErrorBoundary';
import { AddToHomeScreenHint } from './components/shared/AddToHomeScreenHint';
import { PendingNotificationModal } from './components/shared/PendingNotificationModal';
import { ExtensionStatusProvider } from './hooks/useExtensionStatus';

// Surface hooks -- called at route level, not inside surfaces
import { useSourcesSurface } from './hooks/useSourcesSurface';
import { useMapSurface, type MapSurfaceData } from './hooks/useMapSurface';
import { useAskQuery } from './hooks/useAskQuery';
import { useAskUploads } from './hooks/useAskUploads';
import { reconcileUrlToThread, reconcileThreadToUrl } from './lib/ask-url';
import { useTeamTodaySurface } from './hooks/useTeamTodaySurface';
import { useBriefingHistory, useBriefingHistoryDetail, toSurfaceData } from './hooks/useBriefingHistory';
import { useTeamMembersSurface } from './hooks/useTeamMembersSurface';
import { useTeamConversationDetail } from './hooks/useTeamConversationDetail';
import { useConversationDetail } from './hooks/useConversationDetail';
import { useTeamSettings, useCurrentUserId, usePendingInvites, useAcceptInvite, useDeclineInvite } from './hooks/useTeamSettings';
import { useDetailBack } from './hooks/useDetailBack';

// Public pages (no auth required)
import { PrivacyPage } from './components/PrivacyPage';
import { AuthScreen } from './components/AuthScreen';
import { OnboardingGate } from './components/onboarding/OnboardingGate';

// Surface components
import { SourcesSurface } from './components/surfaces/SourcesSurface';
import { SettingsSurface } from './components/surfaces/SettingsSurface';
import { MapSurface } from './components/surfaces/MapSurface';
import { AskSurface } from './components/surfaces/AskSurface';
import { BriefSurface } from './components/surfaces/BriefSurface';
import { BriefingHistorySurface } from './components/surfaces/BriefingHistorySurface';
import { useTeamStandupSurface } from './hooks/useTeamStandupSurface';
import { TeamStandupSurface } from './components/surfaces/TeamStandupSurface';
import { useTeamLiveSurface } from './hooks/useTeamLiveSurface';
import { TeamLiveSurface } from './components/surfaces/TeamLiveSurface';
import { TeamLiveMemberSurface } from './components/surfaces/TeamLiveMemberSurface';
import { LIVE_STANDUP_ENABLED } from './lib/flags';
import { TeamMembersSurface } from './components/surfaces/TeamMembersSurface';
import { ActionsSurface } from './components/surfaces/ActionsSurface';
import { TeamConversationDetailSurface } from './components/surfaces/TeamConversationDetailSurface';
import { ConversationDetailSurface } from './components/surfaces/ConversationDetailSurface';
import { ConversationDeletedNotice } from './components/ConversationDeletedNotice';

// --- Loading/Error states (route-level) ---

function SurfaceLoading({ name }: { name: string }) {
  return (
    <div className="v2-content" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 300 }}>
      <span style={{ color: 'var(--text2)', fontSize: 14, fontFamily: 'var(--mono)', letterSpacing: '0.08em' }}>Loading {name}...</span>
    </div>
  );
}

// The error branch of every hook-backed route. It used to render two <span>s and ZERO interactive
// elements: with refetchOnWindowFocus off, a transient failure latched until the user happened to
// navigate away and back, and an expired session showed the raw string "API error: 401
// Unauthorized" with no route to signing in again. Every failure state now offers a way forward.
function SurfaceError({ name, error }: { name: string; error: Error | null }) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const expired = isUnauthorized(error);

  return (
    <div className="v2-content" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: 300, gap: 12 }}>
      <span style={{ color: 'var(--rose)', fontSize: 14, fontFamily: 'var(--mono)', letterSpacing: '0.08em' }}>
        {expired ? 'Your session expired' : `${name} -- Error Loading Data`}
      </span>
      <span style={{ color: 'var(--text3)', fontSize: 12, maxWidth: 380, textAlign: 'center' }}>
        {expired
          ? 'Sign in again to pick up where you left off.'
          : error?.message ?? 'Something went wrong loading this page.'}
      </span>
      <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
        {expired ? (
          // Supabase's SIGNED_OUT event is what clears the cached token and drops the app to the
          // auth screen — signing out IS the re-auth path.
          <button className="v2-btn" onClick={() => { void supabase.auth.signOut(); }}>Sign in again</button>
        ) : (
          <button className="v2-btn" onClick={() => { void queryClient.refetchQueries({ type: 'active' }); }}>
            Try again
          </button>
        )}
        <button className="v2-btn" onClick={() => navigate('/briefing')}>Back to briefing</button>
      </div>
    </div>
  );
}

// bug-207: the app-wide extension handshake now runs inside ExtensionStatusProvider
// (src/hooks/useExtensionStatus), which both fires the single handshake AND exposes its
// status to surfaces via context (SourcesSurface gates connect on it). It replaces the
// old fire-and-forget ExtensionBridgeRunner.


// --- Route wrappers: call hooks, handle loading/error, pass data to surface ---

function SourcesRoute({ onNav, userEmail }: { onNav: (id: string) => void; userEmail?: string }) {
  const { data, isLoading, error } = useSourcesSurface();
  // Funnel step: arriving at Sources (fires once on mount, before the load/error branches).
  useEffect(() => { track('sources_viewed'); }, []);
  if (isLoading) return <SurfaceLoading name="Sources" />;
  if (error || !data) return <SurfaceError name="Sources" error={error} />;
  return <SourcesSurface data={data} onNav={onNav} userEmail={userEmail} />;
}

function SettingsRoute({ onNav, userEmail, theme, onToggleTheme }: {
  onNav: (id: string) => void; userEmail?: string; theme: Theme; onToggleTheme: () => void;
}) {
  // Gate only on the team identity (always present in the mandatory-teams model) + the current
  // user id. Settings (email, theme, privacy, team) needs no account-surface fetch.
  const { data: teamData, isLoading: teamLoading, error: teamError } = useTeamSettings();
  const { data: currentUserId, isLoading: userLoading } = useCurrentUserId();
  // Navigation baseline event (fires once on mount, before the load/error branches).
  useEffect(() => { track('settings_viewed'); }, []);
  if (teamLoading || userLoading) return <SurfaceLoading name="Settings" />;
  if (teamError || !teamData || !currentUserId) return <SurfaceError name="Settings" error={teamError} />;
  return (
    <SettingsSurface
      teamData={teamData}
      currentUserId={currentUserId}
      userEmail={userEmail}
      theme={theme}
      onToggleTheme={onToggleTheme}
      onNav={onNav}
    />
  );
}

// --- Team route wrappers ---

function TeamTodayRoute({ onNav, userName }: { onNav: (id: string) => void; userName?: string }) {
  const { data, isLoading, error } = useTeamTodaySurface();
  // The masthead greeting (§B5) is keyed on the reader's id, so the route resolves it ALONGSIDE the
  // brief and gates on both — a greeting that swapped after the page painted would read as a second,
  // contradicting headline. A failed/absent id still renders (null key), it just loses the spread.
  const { data: readerId, isLoading: readerLoading } = useCurrentUserId();
  // Funnel tail: viewed the briefing (a proxy for first-brief until the Phase-2 server event exists).
  useEffect(() => { track('briefing_viewed'); }, []);
  if (isLoading || readerLoading) return <SurfaceLoading name="Today" />;
  if (error || !data) return <SurfaceError name="Today" error={error} />;
  return <BriefSurface data={data} onNav={onNav} userName={userName} readerId={readerId ?? null} />;
}

function BriefingHistoryRoute({ onNav }: { onNav: (id: string) => void }) {
  const { data, isLoading, error } = useBriefingHistory();
  if (isLoading) return <SurfaceLoading name="Briefing History" />;
  if (error || !data) return <SurfaceError name="Briefing History" error={error} />;
  return <BriefingHistorySurface data={data} onNav={onNav} />;
}

// Standup is a CHILD of the briefing (lib/surfaces), so its cold-load back names that parent
// rather than dumping the reader at the top of the app.
function TeamStandupRoute({ onNav }: { onNav: (id: string) => void }) {
  const { data, isLoading, error } = useTeamStandupSurface();
  const back = useDetailBack('/briefing', 'Briefing');
  if (isLoading) return <SurfaceLoading name="Standup" />;
  if (error || !data) return <SurfaceError name="Standup" error={error} />;
  return <TeamStandupSurface data={data} onNav={onNav} back={back} />;
}

// Actions workroom (redesign): conflicts + tasks from the (cached) briefing payload; the
// surface itself wires the conflict card/resolve rails per room.
function ActionsRoute({ onNav }: { onNav: (id: string) => void }) {
  const { data, isLoading, error } = useTeamTodaySurface();
  if (isLoading) return <SurfaceLoading name="Actions" />;
  if (error || !data) return <SurfaceError name="Actions" error={error} />;
  return <ActionsSurface data={data} onNav={onNav} />;
}

// LIVE standup board (/live) — separate surface from the retrospective standup above.
function TeamLiveRoute({ onNav }: { onNav: (id: string) => void }) {
  const { data, isLoading, error } = useTeamLiveSurface();
  const { data: currentUserId } = useCurrentUserId();
  if (isLoading) return <SurfaceLoading name="Live" />;
  if (error || !data) return <SurfaceError name="Live" error={error} />;
  return <TeamLiveSurface data={data} onNav={onNav} currentUserId={currentUserId ?? undefined} />;
}

// LIVE member page (/live/:userId) — slices the CACHED board query (no extra fetch; one data
// source, per spec §4.7). A self-navigated own id misses (self excluded by D3) → graceful error.
function TeamLiveMemberRoute({ onNav }: { onNav: (id: string) => void }) {
  const { userId } = useParams<{ userId: string }>();
  const { data, isLoading, error } = useTeamLiveSurface();
  const { data: currentUserId } = useCurrentUserId();
  if (isLoading) return <SurfaceLoading name="Live" />;
  if (error || !data) return <SurfaceError name="Live" error={error} />;
  const member = (data.members ?? []).find((m) => m.user_id === userId);
  if (!member) return <SurfaceError name="Live" error={new Error('No live status for this teammate')} />;
  // Team-shared installs (the Slack workspace bot, Notion) live on ONE member's integration row but
  // cover the whole team, so a per-member payload can't distinguish "this person hasn't connected
  // Notion" from "the team has it, just not via them". Union the connected sites across every member
  // so the page never renders a team-covered source as a personal gap. (The backend already special-
  // cases workspace Slack per D2; Notion has no such carve-out, hence this.)
  const teamConnectedSites = (data.members ?? []).flatMap((m) =>
    (m.sources ?? []).filter((s) => s.connected).map((s) => s.site)
  );
  return (
    <TeamLiveMemberSurface
      member={member}
      generatedAt={data.generated_at ?? null}
      you={member.user_id === currentUserId}
      teamConnectedSites={teamConnectedSites}
      onNav={onNav}
    />
  );
}

function BriefingHistoryDetailRoute({ onNav, userName }: { onNav: (id: string) => void; userName?: string }) {
  const { id } = useParams<{ id: string }>();
  const { data, isLoading, error } = useBriefingHistoryDetail(id || '');
  // Same reader key as the live route: an archived brief re-derives its ORIGINAL greeting from its
  // OWN date, so it must be picked for the same reader or history rewrites itself (§B5).
  const { data: readerId, isLoading: readerLoading } = useCurrentUserId();
  if (isLoading || readerLoading) return <SurfaceLoading name="Briefing" />;
  // A 404 here means the briefing id is unknown or not the caller's — a plain
  // "not found", deliberately NOT the ConversationDeletedNotice used by the
  // conversation routes (that state is specific to a purged conversation link).
  if (isNotFound(error)) return <SurfaceError name="Briefing" error={new Error('Briefing not found')} />;
  if (error || !data) return <SurfaceError name="Briefing" error={error} />;
  // Reuse the team/today renderer for a past briefing — readOnly so it never marks
  // today viewed; the masthead crumb shows the briefing's own date. BriefSurface itself
  // branches on the row's version (legacy TeamTodaySurface for pre-standardization rows).
  return (
    <BriefSurface
      data={toSurfaceData(data)}
      onNav={onNav}
      userName={userName}
      readerId={readerId ?? null}
      readOnly
    />
  );
}

function TeamMembersRoute({ onNav }: { onNav: (id: string) => void }) {
  const { data, isLoading, error } = useTeamMembersSurface();
  // Invite affordance: teamId + pending invites + admin status come from the
  // team-settings payload (same source the Settings route uses; cached/shared).
  const { data: settings } = useTeamSettings();
  const { data: currentUserId } = useCurrentUserId();
  if (isLoading) return <SurfaceLoading name="Members" />;
  if (error || !data) return <SurfaceError name="Members" error={error} />;
  const isAdmin = !!settings && !!currentUserId &&
    settings.members.find((m) => m.user_id === currentUserId)?.role === 'admin';
  return (
    <TeamMembersSurface
      data={data}
      onNav={onNav}
      currentUserId={currentUserId ?? undefined}
      teamId={settings?.id}
      teamName={settings?.name}
      isAdmin={isAdmin}
      pendingInvites={settings?.pending_invites ?? []}
    />
  );
}

export function TeamConversationDetailRoute({ onNav }: { onNav: (id: string) => void }) {
  const { id } = useParams<{ id: string }>();
  const team = useTeamConversationDetail(id || '');
  // `/conversations/:id` is the ONE nav target the whole app uses for a note — every brief citation
  // row, every panel row, every "Open the note" action — but it reads the TEAM detail BFF, whose
  // shared-surface predicate (private=false OR audience contains viewer) excludes the viewer's OWN
  // private notes: an own inbox note carries audience=[owner], so has_audience is false and the BFF
  // 404s. That rendered "This conversation was deleted" over a live note the reader owns. So a 404
  // here is NOT proof the note is gone — re-ask the PERSONAL endpoint (.eq user_id = viewer), which
  // serves exactly those rows, before believing it. Passing '' keeps the second query disabled (the
  // hook's `enabled: !!id`) until the team read actually misses, so the common path stays one request.
  const teamMissed = isNotFound(team.error);
  const mine = useConversationDetail(teamMissed ? id || '' : '');
  const back = useDetailBack();
  if (team.isLoading) return <SurfaceLoading name="Team Conversation" />;
  if (teamMissed) {
    if (mine.isLoading) return <SurfaceLoading name="Conversation" />;
    if (mine.data) return <ConversationDetailSurface data={mine.data} onNav={onNav} mode="individual" back={back} />;
    // A real infra failure on the fallback must not masquerade as "deleted" (bug-183's rule).
    if (mine.error && !isNotFound(mine.error)) return <SurfaceError name="Conversation" error={mine.error} />;
    // Both endpoints miss ⇒ genuinely gone (its author deleted their data, leaving a dangling link
    // in an older briefing) or never visible to this viewer: the friendly dead-link state. It gets
    // the SAME back as the live page — it used to say "Back to conversations" and open the graph.
    return <ConversationDeletedNotice back={back} />;
  }
  if (team.error || !team.data) return <SurfaceError name="Team Conversation" error={team.error} />;
  return <TeamConversationDetailSurface data={team.data} onNav={onNav} back={back} />;
}

// Personal conversation detail — the user's OWN conversation (incl. private), via the
// personal endpoint (/api/surfaces/conversations/:id). Settings' "Private conversations"
// links straight here rather than paying the team route's 404-then-fallback round trip
// (that fallback, added above, is what keeps /conversations/:id honest for brief links).
function ConversationDetailRoute({ onNav }: { onNav: (id: string) => void }) {
  const { id } = useParams<{ id: string }>();
  const { data, isLoading, error } = useConversationDetail(id || '');
  // Cold-load fallback is Settings, not the map list: this route serves the user's OWN notes
  // (including private ones), and the team conversations BFF excludes an owner-only private note
  // even from its author — so a reloaded private note used to back out onto a list that by design
  // could not contain it. Settings' "Private conversations" list is where it actually lives, and
  // it already matches this route's own 404 branch.
  const back = useDetailBack('/settings', 'Settings');
  if (isLoading) return <SurfaceLoading name="Conversation" />;
  // A purged conversation (its author deleted their data) leaves a dangling link in
  // older briefings — the BFF 404s; show a friendly dead-link state, not an error.
  if (isNotFound(error)) return <ConversationDeletedNotice back={back} />;
  if (error || !data) return <SurfaceError name="Conversation" error={error} />;
  return <ConversationDetailSurface data={data} onNav={onNav} mode="individual" back={back} />;
}

function TeamConvDetailRedirect() {
  const { id } = useParams<{ id: string }>();
  return <Navigate to={`/conversations/${id}`} replace />;
}

function TeamMapRoute({ onNav }: { onNav: (id: string) => void }) {
  const { data, isLoading, error } = useMapSurface();
  if (isLoading) return <SurfaceLoading name="Team Map" />;
  if (error || !data) return <SurfaceError name="Team Map" error={error} />;
  return <MapSurface data={data} onNav={onNav} />;
}

// WARM empty-state starter prompts: the team's top project names ("What's happening with X?") + two static
// fallbacks, capped at 4. On a cold/empty corpus AskSurface shows the COLD state instead and ignores these.
function buildStarters(data: MapSurfaceData | undefined): string[] {
  const names: string[] = [];
  for (const n of data?.nodes ?? []) {
    const name = n.team_project_name || n.project_name;
    if (name && !names.includes(name)) names.push(name);
    if (names.length >= 2) break;
  }
  const chips = names.map((n) => `What's happening with ${n}?`);
  chips.push('What are the open decisions right now?', "What's currently blocked, and who's on it?");
  return chips.slice(0, 4);
}

// Ask peer surface (/ask, /ask/:chatId). Unlike the other surfaces it owns a STREAMING hook (useAskQuery),
// not a React Query fetch — so there is no isLoading/error wrapper; the surface renders its own four states.
// The URL is the source of truth for which thread is shown: AskRoute drives the hook from the :chatId param
// and reflects a server-minted id back into the URL. `loadedRef` is the last chatId reconciled, so a nav we
// performed is never mistaken for a browser back/forward; the pure decisions live in lib/ask-url (tested).
function AskRoute({ onNav }: { onNav: (id: string) => void }) {
  const { chatId } = useParams<{ chatId?: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const ask = useAskQuery();
  // The composer's attachment lane (U6/D32-lite). It keys on the active thread and, on a bare-thread
  // attach, mints a chat via the uploads route and adopts it here — which the thread→URL effect below
  // then reflects into /ask/<chatId>, exactly as a first-answer mint does.
  const uploads = useAskUploads(ask.activeChatId, ask.adoptChat);
  const map = useMapSurface();
  const loadedRef = useRef<string | null>(null);

  // Navigation baseline event (fires once on mount, before any thread loads).
  useEffect(() => { track('ask_viewed'); }, []);

  // URL -> thread: load a deep-linked / rail-selected / back-forward thread, or reset to empty on bare /ask.
  useEffect(() => {
    const action = reconcileUrlToThread(chatId, loadedRef.current);
    if (action.type === 'load') { loadedRef.current = action.chatId; ask.loadChat(action.chatId); }
    else if (action.type === 'reset') { loadedRef.current = null; ask.newChat(); }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- ask.loadChat/newChat are stable useCallbacks
  }, [chatId]);

  // thread -> URL: reflect the id the server mints on the first persisted turn of a bare /ask thread, so the
  // conversation becomes deep-linkable mid-thread (replace = no history entry). No-op once the URL matches.
  useEffect(() => {
    const action = reconcileThreadToUrl(ask.activeChatId, chatId);
    if (action.type === 'sync-url') {
      loadedRef.current = action.chatId;
      navigate(`/ask/${action.chatId}`, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- navigate is stable
  }, [ask.activeChatId, chatId]);

  // Cold-start guard: a just-onboarded team with no graph nodes gets the COLD state (composer disabled, no
  // starter prompts), never a suggestion that answers "I found nothing". Fail-open to WARM while the map is
  // still loading or on a map error — a usable composer beats a locked one on a false signal.
  const corpusEmpty = !!map.data && (map.empty === true || (map.data.nodes?.length ?? 0) === 0);
  const starters = useMemo(() => buildStarters(map.data), [map.data]);

  return (
    <AskSurface
      ask={ask}
      onNav={onNav}
      initialQuery={searchParams.get('q')}
      // On a persisted thread the URL change drives the reset effect below. On a thread that never
      // minted an id — an errored turn, a Stop before the first token, an answer whose save failed,
      // i.e. exactly the states you press "New chat" to escape — navigating to /ask leaves :chatId
      // undefined, the effect keyed on [chatId] never re-runs, and the button did nothing at all.
      onNewChat={() => {
        if (chatId) { navigate('/ask'); return; }
        loadedRef.current = null;
        ask.newChat();
      }}
      onSelectChat={(id) => navigate(`/ask/${id}`)}
      corpusEmpty={corpusEmpty}
      starters={starters}
      uploads={uploads}
    />
  );
}

// --- Global invite banner ---

function GlobalInviteBanner() {
  const navigate = useNavigate();
  const { data: pendingData } = usePendingInvites();
  const accept = useAcceptInvite();
  const decline = useDeclineInvite();
  const [confirmId, setConfirmId] = useState<string | null>(null);
  const [dismissed, setDismissed] = useState<Set<string>>(new Set());

  if (!pendingData || pendingData.invites.length === 0) return null;

  const visible = pendingData.invites.filter(inv => !dismissed.has(inv.id));
  if (visible.length === 0) return null;

  const currentTeam = pendingData.current_team;
  const leaveMsg = currentTeam
    ? currentTeam.member_count <= 1
      ? `Your current team "${currentTeam.name}" will be deleted (you're the only member).`
      : `You'll leave "${currentTeam.name}" (${currentTeam.member_count - 1} member${currentTeam.member_count - 1 !== 1 ? 's' : ''} remaining).`
    : null;

  return (
    <div style={{ padding: '0 32px', marginTop: 8 }}>
      {visible.map(inv => (
        <div key={inv.id} style={{
          padding: '20px 24px', marginBottom: 12, borderRadius: 10,
          background: 'linear-gradient(135deg, rgba(131,119,209,0.10) 0%, rgba(131,119,209,0.04) 100%)',
          border: '1px solid rgba(131,119,209,0.30)',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 16 }}>
            <div style={{ flex: 1, minWidth: 200 }}>
              <div style={{ fontFamily: 'var(--display)', fontSize: 18, fontWeight: 600, color: 'var(--text)', marginBottom: 4 }}>
                You've been invited to join {inv.team_name}
              </div>
              <div style={{ fontFamily: 'var(--sans)', fontSize: 13, color: 'var(--text3)' }}>
                Invited by {inv.invited_by_email}
              </div>
              {confirmId === inv.id && leaveMsg && (
                <div style={{
                  marginTop: 10, padding: '8px 12px', borderRadius: 5,
                  background: 'rgba(255,184,74,0.08)', border: '1px solid rgba(255,184,74,0.25)',
                  fontFamily: 'var(--sans)', fontSize: 12, color: 'var(--amber)',
                }}>
                  {leaveMsg}
                </div>
              )}
              {accept.isError && confirmId === inv.id && (
                <div style={{ marginTop: 8, fontFamily: 'var(--sans)', fontSize: 12, color: 'var(--rose)' }}>
                  Failed to join team. Try again.
                </div>
              )}
              {/* Decline used to dismiss the row optimistically with no error branch at all, so a
                  failed decline looked done and the invite reappeared on the next load. */}
              {decline.isError && (
                <div style={{ marginTop: 8, fontFamily: 'var(--sans)', fontSize: 12, color: 'var(--rose)' }}>
                  Couldn&rsquo;t decline that invite. It&rsquo;s still open - try again.
                </div>
              )}
            </div>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              {confirmId === inv.id ? (
                <>
                  <button onClick={() => setConfirmId(null)} className="v2-btn">CANCEL</button>
                  <button
                    // useAcceptInvite invalidates the whole query cache (the user's team changed, so
                    // every team-scoped query is stale) — which is what this full-document reload was
                    // really buying, at the cost of tearing down the SPA and losing the user's place.
                    onClick={() => accept.mutate({ teamId: inv.team_id, inviteId: inv.id }, {
                      onSuccess: () => navigate('/briefing'),
                    })}
                    disabled={accept.isPending}
                    className="v2-btn"
                    style={{ background: 'var(--accent)', color: '#fff', border: 'none', padding: '8px 20px' }}
                  >{accept.isPending ? 'JOINING...' : 'CONFIRM JOIN'}</button>
                </>
              ) : (
                <>
                  <button
                    onClick={() => {
                      // Dismiss only once the server confirms — an optimistic hide made a failed
                      // decline indistinguishable from a successful one until the next page load.
                      decline.mutate({ teamId: inv.team_id, inviteId: inv.id }, {
                        onSuccess: () => setDismissed(prev => new Set(prev).add(inv.id)),
                      });
                    }}
                    disabled={decline.isPending}
                    className="v2-btn"
                  >{decline.isPending ? 'DECLINING...' : 'DECLINE'}</button>
                  <button
                    onClick={() => setConfirmId(inv.id)}
                    className="v2-btn"
                    style={{ background: 'var(--accent)', color: '#fff', border: 'none', padding: '8px 20px' }}
                  >ACCEPT</button>
                </>
              )}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

// --- Briefing wrong-account banner (bug-177) ---

// Shown when a briefing link's intended recipient (decoded from ?b=<token>) differs from
// the signed-in dashboard user. Mirrors GlobalInviteBanner's styling for consistency.
function BriefingMismatchBanner({ intendedEmail, token }: { intendedEmail: string | null; token: string | null }) {
  const who = intendedEmail ? <strong>{intendedEmail}</strong> : <span>a different account</span>;
  const switchAccount = () => {
    // Re-stash the briefing token before signing out. It was consumed and stripped from the URL
    // when this banner was raised, so without this the user switched accounts and landed on TODAY's
    // briefing rather than the one they were sent — which is the entire purpose of the banner.
    if (token) stashBriefingTokenForLogin(token);
    // Sign out; the auth gate then re-renders AuthScreen (Google OAuth) for the right account.
    supabase.auth.signOut().finally(() => window.location.reload());
  };
  return (
    <div style={{ padding: '0 32px', marginTop: 8 }}>
      <div style={{
        padding: '20px 24px', marginBottom: 12, borderRadius: 10,
        background: 'rgba(255,184,74,0.08)', border: '1px solid rgba(255,184,74,0.30)',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 16 }}>
          <div style={{ flex: 1, minWidth: 200 }}>
            <div style={{ fontFamily: 'var(--display)', fontSize: 18, fontWeight: 600, color: 'var(--text)', marginBottom: 4 }}>
              This briefing was sent to {who}
            </div>
            <div style={{ fontFamily: 'var(--sans)', fontSize: 13, color: 'var(--text3)' }}>
              You're signed in as a different account, so this is not the briefing you were sent. Switch accounts to see it.
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <button
              onClick={switchAccount}
              className="v2-btn"
              style={{ background: 'var(--accent)', color: '#fff', border: 'none', padding: '8px 20px' }}
            >SWITCH ACCOUNT</button>
          </div>
        </div>
      </div>
    </div>
  );
}

// --- Analytics identity (PostHog) ---

// Identifies the signed-in user to PostHog (powers retention + funnel) and groups them by team.
// Mounted ONLY inside the authenticated tree so useTeamSettings never fires an unauthenticated
// request, but OUTSIDE OnboardingGate so activation events during onboarding are still attributed.
// Renders nothing. No PII beyond the ids — structure-only, same rule as every other event.
function AnalyticsIdentity() {
  const { data: userId } = useCurrentUserId();
  const { data: team } = useTeamSettings();
  useEffect(() => { if (userId) identifyUser(userId); }, [userId]);
  useEffect(() => { if (team?.id) setTeamGroup(team.id); }, [team?.id]);
  return null;
}

// --- App Layout with auth gate ---

function AppLayout() {
  const [authReady, setAuthReady] = useState(false);
  const [authenticated, setAuthenticated] = useState(false);
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const location = useLocation();

  // Theme state (no voice state)
  const [theme, setTheme] = useState<Theme>(resolveInitialTheme);
  const [cmdkOpen, setCmdkOpen] = useState(false);
  // True while first-run onboarding takes over the screen (reported by OnboardingGate). Feeds
  // session replay so the wizard — a fresh, content-free user's highest-value replay — records
  // even though it renders outside the router. Declared before the privacy early-return below so
  // the replay hook's inputs stay in a stable hook order.
  const [onboardingActive, setOnboardingActive] = useState(false);

  // The FIRST path segment, not "the path with one slash removed". `String.replace` with a string
  // pattern replaces a single occurrence, so `/conversations/abc` yielded the route id
  // "conversations/abc" — which matches no nav id, so TopNav and BottomTabBar silently lost their
  // active state on every parameterised route (`/conversations/:id`, `/my-conversations/:id`,
  // `/live/:userId`, `/briefing-history/:id`, `/team-conversations/:id`, `/invite/:id`). It also made
  // `key={route}` on the surface ErrorBoundary change per id, remounting it on every detail navigation.
  const route = location.pathname.split('/')[1] || 'briefing';
  const onNav = (id: string) => navigate(`/${id}`);
  const isMobile = useIsMobile();

  // Manual pageview on hash-route change — HashRouter never fires PostHog's autocapture $pageview,
  // so nav would otherwise record nothing. `location.pathname` is the in-hash path (e.g. "/briefing").
  useEffect(() => { pageview(location.pathname); }, [location.pathname]);
  // Session replay is route-scoped + DEFAULT-DENY (see useSessionReplayScope): recorded only on
  // low-sensitivity routes, never on content routes. Placed before the privacy early-return so it
  // runs on every render (consistent hook order).
  useSessionReplayScope(authenticated, onboardingActive);

  const [userProfile, setUserProfile] = useState<{ name: string; initials: string; plan: string; email?: string } | undefined>();
  // bug-177: null = no mismatch (render normally); string|null = wrong-account banner email.
  const [briefingMismatchEmail, setBriefingMismatchEmail] = useState<string | null>(null);
  const [briefingMismatch, setBriefingMismatch] = useState(false);
  // Kept so SWITCH ACCOUNT can re-stash it across the sign-out (the URL copy is stripped below).
  const [briefingMismatchToken, setBriefingMismatchToken] = useState<string | null>(null);

  useEffect(() => {
    if (!authenticated) return;
    supabase.auth.getUser().then(({ data: { user } }) => {
      if (!user) return;
      const fullName = user.user_metadata?.full_name || user.email?.split('@')[0] || 'User';
      const parts = fullName.split(' ');
      const initials = parts.length >= 2 ? (parts[0][0] + parts[parts.length - 1][0]).toUpperCase() : fullName.slice(0, 2).toUpperCase();
      setUserProfile({ name: fullName, initials, plan: '', email: user.email ?? '' });
    });
  }, [authenticated]);

  // bug-177: reconcile a briefing link's intended recipient (?b=<token>) against the
  // signed-in user. Match/no-token -> strip param and render normally. Mismatch -> banner.
  // Fully fail-safe: verifyBriefingIntent never throws and returns null if the MVP
  // verify-intent endpoint is absent/erroring (it deploys before this SPA).
  useEffect(() => {
    if (!authenticated) return;
    // Token from the URL, or one stashed before the OAuth round-trip (which drops the hash).
    const token = readBriefingTokenFromUrl() ?? popStashedBriefingToken();
    if (!token) return;
    let cancelled = false;
    (async () => {
      const intent = await verifyBriefingIntent(token);
      if (cancelled) return;
      const { data: { user } } = await supabase.auth.getUser();
      if (cancelled) return;
      const result = reconcileBriefingIntent(intent, user?.id ?? null);
      if (result.kind === 'mismatch') {
        setBriefingMismatchEmail(result.intendedEmail);
        setBriefingMismatch(true);
        setBriefingMismatchToken(token);
      }
      // Strip the token from the URL in every case (match, none, or mismatch) so a
      // refresh/share doesn't re-trigger or leak the opaque blob.
      stripBriefingTokenFromUrl();
    })();
    return () => { cancelled = true; };
  }, [authenticated]);

  // Theme effect
  useEffect(() => { applyTheme(theme); }, [theme]);

  const toggleTheme = () => setTheme(t => t === 'dark' ? 'light' : 'dark');

  // Cmd+K shortcut
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setCmdkOpen(o => !o);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // Auth gate with dev bypass
  useEffect(() => {
    if (isDevBypass()) {
      setAuthenticated(true);
      setAuthReady(true);
      return;
    }

    supabase.auth.getSession().then(({ data: { session } }) => {
      console.log("[App auth] getSession:", session ? "authenticated" : "no session");
      setAccessToken(session?.access_token ?? null);
      setAuthenticated(!!session);
      setAuthReady(true);
    });

    const subscription = onAuthChange((event, session: any) => {
      if (event === 'SIGNED_OUT') {
        setAccessToken(null);
        queryClient.clear();
        resetUser(); // drop the PostHog identity so the next user isn't merged into this one
        setAuthenticated(false);
        // bug-171 Phase 5: tell the extension to clear its stored tokens + identity
        // caches so capture PAUSES until a fresh handshake — otherwise the
        // previously-handshook account's token keeps refreshing and capturing into
        // the signed-out user's account (the stale-binding live leak). Relayed by
        // content-announce.js to the background service worker.
        try {
          window.postMessage({ type: 'terum-sign-out' }, window.location.origin);
        } catch {
          // postMessage can't fail meaningfully here; guarded for SSR/test envs.
        }
      } else if (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED') {
        setAccessToken(session?.access_token ?? null);
        setAuthenticated(true);
      }
    });

    return () => subscription.unsubscribe();
  }, [queryClient]);

  // ---- Every hook above this line runs on EVERY render. Early returns start here. ----

  // /privacy is public: it renders before the auth gate for signed-out visitors. This check used to
  // sit halfway up the component, between two blocks of hooks — so hash-navigating to or from
  // #/privacy changed the number of hooks AppLayout called between renders, React threw "rendered
  // fewer hooks than expected", and the top-level ErrorBoundary replaced the whole app with a
  // reload screen. It renders identically here, with the hook count now constant.
  if (route === 'privacy') return <PrivacyPage />;

  // Loading state while checking auth
  if (!authReady) {
    return (
      <div className="v2-app" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh' }}>
        <span style={{ color: 'var(--text2)', fontSize: 14 }}>Loading...</span>
      </div>
    );
  }

  // Invite page: renders for both authenticated and unauthenticated users
  if (window.location.hash.startsWith('#/invite/')) {
    return (
      <Routes>
        <Route path="/invite/:id" element={<InvitePage authenticated={authenticated} />} />
        <Route path="*" element={<Navigate to="/briefing" replace />} />
      </Routes>
    );
  }

  // Not authenticated: render the Google-only auth front door (OAuth fires on click).
  if (!authenticated) {
    return <AuthScreen />;
  }

  // Authenticated (or dev bypass): render dashboard, hard-locked behind onboarding
  // completion. The shell/router live INSIDE the gate so nothing mounts until a
  // source connects — the wizard takes over the screen until then.
  return (
    <>
      <AnalyticsIdentity />
      <OnboardingGate onActiveChange={setOnboardingActive}>
      <ExtensionStatusProvider>
      <div className="v2-app">
        <TopNav route={route} theme={theme} onNav={onNav} onToggleTheme={toggleTheme} onCmdK={() => setCmdkOpen(true)}
          profile={userProfile} onSignOut={() => { void supabase.auth.signOut(); }} isMobile={isMobile} />
        <main className="v2-main">
          {briefingMismatch && <BriefingMismatchBanner intendedEmail={briefingMismatchEmail} token={briefingMismatchToken} />}
          <GlobalInviteBanner />
          <ErrorBoundary key={route} label="This surface">
          <Routes>
            {/* The 5 surfaces */}
            <Route path="/briefing" element={<TeamTodayRoute onNav={onNav} userName={userProfile?.name} />} />
            {/* Briefing history (reachable from the Briefing surface header) */}
            <Route path="/briefing-history" element={<BriefingHistoryRoute onNav={onNav} />} />
            {/* Actions workroom (redesign): explicit actions + the record. */}
            <Route path="/actions" element={<ActionsRoute onNav={onNav} />} />
            <Route path="/standup" element={<TeamStandupRoute onNav={onNav} />} />
            {/* LIVE standup board — flag-gated dark until the BFF route is live in prod (§6 step 5).
                When off, /live falls through to the catch-all → /briefing. */}
            {LIVE_STANDUP_ENABLED && <Route path="/live" element={<TeamLiveRoute onNav={onNav} />} />}
            {LIVE_STANDUP_ENABLED && <Route path="/live/:userId" element={<TeamLiveMemberRoute onNav={onNav} />} />}
            <Route path="/briefing-history/:id" element={<BriefingHistoryDetailRoute onNav={onNav} userName={userProfile?.name} />} />
            {/* Ask — agentic Q&A peer surface. /ask = new thread; /ask/:chatId = one persisted thread;
                /ask?q= prefills the composer (never auto-submits). */}
            <Route path="/ask" element={<AskRoute onNav={onNav} />} />
            <Route path="/ask/:chatId" element={<AskRoute onNav={onNav} />} />
            <Route path="/map" element={<TeamMapRoute onNav={onNav} />} />
            <Route path="/people" element={<TeamMembersRoute onNav={onNav} />} />
            <Route path="/sources" element={<SourcesRoute onNav={onNav} userEmail={userProfile?.email} />} />
            <Route path="/settings" element={<SettingsRoute onNav={onNav} userEmail={userProfile?.email} theme={theme} onToggleTheme={toggleTheme} />} />
            {/* Conversation detail (briefing deep-link) */}
            <Route path="/conversations/:id" element={<TeamConversationDetailRoute onNav={onNav} />} />
            <Route path="/my-conversations/:id" element={<ConversationDetailRoute onNav={onNav} />} />
            {/* Auth-independent (unchanged) */}
            <Route path="/invite/:id" element={<InvitePage authenticated={authenticated} />} />
            <Route path="/privacy" element={<PrivacyPage />} />
            {/* Redirects: old paths → new 5 */}
            <Route path="/team-conversations/:id" element={<TeamConvDetailRedirect />} />
            <Route path="/team-today" element={<Navigate to="/briefing" replace />} />
            <Route path="/home" element={<Navigate to="/briefing" replace />} />
            <Route path="/pulse" element={<Navigate to="/briefing" replace />} />
            <Route path="/team-home" element={<Navigate to="/briefing" replace />} />
            <Route path="/team-analytics" element={<Navigate to="/briefing" replace />} />
            <Route path="/memory" element={<Navigate to="/briefing" replace />} />
            <Route path="/chat" element={<Navigate to="/briefing" replace />} />
            <Route path="/apps" element={<Navigate to="/briefing" replace />} />
            <Route path="/automation" element={<Navigate to="/briefing" replace />} />
            <Route path="/insights" element={<Navigate to="/briefing" replace />} />
            <Route path="/learning" element={<Navigate to="/briefing" replace />} />
            <Route path="/team-map" element={<Navigate to="/map" replace />} />
            <Route path="/team-conversations" element={<Navigate to="/map" replace />} />
            <Route path="/conversations" element={<Navigate to="/map" replace />} />
            <Route path="/team-members" element={<Navigate to="/people" replace />} />
            <Route path="/identity" element={<Navigate to="/people" replace />} />
            <Route path="/account" element={<Navigate to="/settings" replace />} />
            <Route path="/team-settings" element={<Navigate to="/settings" replace />} />
            <Route path="/trust" element={<Navigate to="/settings" replace />} />
            <Route path="/support" element={<Navigate to="/settings" replace />} />
            <Route path="/" element={<Navigate to="/briefing" replace />} />
            <Route path="*" element={<Navigate to="/briefing" replace />} />
          </Routes>
          </ErrorBoundary>
        </main>
        {cmdkOpen && <CmdK open={cmdkOpen} onClose={() => setCmdkOpen(false)} onNav={onNav} />}
        {isMobile && <BottomTabBar route={route} onNav={onNav} />}
        {isMobile && <AddToHomeScreenHint />}
      </div>
      </ExtensionStatusProvider>
      </OnboardingGate>
      {/* Outside the gate: an ex-member stuck in onboarding must still learn their
          team was deleted. .ext-modal-overlay is position:fixed so tree position is
          irrelevant; it renders on top regardless. */}
      <PendingNotificationModal />
    </>
  );
}

export function App() {
  return (
    <HashRouter>
      <AppLayout />
    </HashRouter>
  );
}
